import { useState } from 'react';
import { Link } from 'react-router-dom';
import { appLinks } from '../lib/crossSiteLinks';

interface NavSection {
  id: string;
  label: string;
  href?: string;
}

interface PublicHeaderProps {
  sections?: NavSection[];
  activeSection?: string;
  mobileMenuOpen?: boolean;
  setMobileMenuOpen?: (open: boolean) => void;
  showContactLink?: boolean;
  contactHref?: string;
  contactLabel?: string;
}

const defaultNavLinks: NavSection[] = [
  { id: 'features', label: 'Features', href: '/features' },
  { id: 'solutions', label: 'Solutions', href: '/solutions' },
  { id: 'tools', label: 'Tools', href: '/tools' },
  { id: 'faq', label: 'FAQ', href: '/faq' }
];

export default function PublicHeader({
  sections,
  activeSection,
  mobileMenuOpen,
  setMobileMenuOpen,
  showContactLink = false,
  contactHref = '/#contact',
  contactLabel = 'Contact'
}: PublicHeaderProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = typeof setMobileMenuOpen === 'function' && typeof mobileMenuOpen === 'boolean';
  const menuOpen = isControlled ? mobileMenuOpen : internalOpen;
  const setMenuOpen = isControlled ? setMobileMenuOpen : setInternalOpen;
  const navItems = sections && sections.length > 0 ? sections : defaultNavLinks;
  const isSectionNav = sections && sections.length > 0;

  return (
    <header className="sticky top-0 z-50 bg-slate-900/80 backdrop-blur-md border-b border-slate-800/50">
      <nav className="max-w-[1140px] mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 group">
          <div className="w-10 h-10 bg-blue-500 rounded-xl flex items-center justify-center transition-transform group-hover:scale-105">
            <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <span className="text-xl font-semibold text-white hidden sm:block tracking-tight">EvidentTrail</span>
        </Link>

        <div className="hidden md:flex items-center text-sm font-medium">
          {navItems.map(({ id, label, href }) => (
            href ? (
              <Link
                key={id}
                to={href}
                className="px-4 py-2 transition-colors font-medium text-slate-300 hover:text-white"
              >
                {label}
              </Link>
            ) : (
              <a
                key={id}
                href={`#${id}`}
                className={`px-4 py-2 transition-colors font-medium relative ${activeSection === id
                  ? 'text-white'
                  : 'text-slate-300 hover:text-white'
                }`}
              >
                {label}
                {activeSection === id && (
                  <span className="absolute bottom-0 left-4 right-4 h-0.5 bg-primary-400 rounded-full" />
                )}
              </a>
            )
          ))}
        </div>

        <div className="relative md:hidden">
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="text-slate-300 hover:text-white px-3 py-2 text-sm font-medium flex items-center gap-1"
          >
            {isSectionNav ? 'Jump to' : 'Menu'}
            <svg className={`w-4 h-4 transition-transform ${menuOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {menuOpen && (
            <div className="absolute top-full right-0 mt-1 bg-slate-800 border border-slate-700 rounded-lg shadow-xl py-2 min-w-[180px] z-50">
              {navItems.map(({ id, label, href }) => (
                href ? (
                  <Link
                    key={id}
                    to={href}
                    onClick={() => setMenuOpen(false)}
                    className="block px-4 py-2 text-sm text-slate-300 hover:text-white hover:bg-slate-700/30"
                  >
                    {label}
                  </Link>
                ) : (
                  <a
                    key={id}
                    href={`#${id}`}
                    onClick={() => setMenuOpen(false)}
                    className={`block px-4 py-2 text-sm transition-colors ${activeSection === id
                      ? 'text-white bg-slate-700/50'
                      : 'text-slate-300 hover:text-white hover:bg-slate-700/30'
                    }`}
                  >
                    {label}
                  </a>
                )
              ))}
              {showContactLink && (
                <>
                  <div className="border-t border-slate-700 my-2" />
                  <a
                    href={contactHref}
                    onClick={() => setMenuOpen(false)}
                    className="block px-4 py-2 text-sm text-slate-300 hover:text-white hover:bg-slate-700/30"
                  >
                    {contactLabel}
                  </a>
                </>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 sm:gap-3">
          {showContactLink && (
            <a href={contactHref} className="text-slate-300 hover:text-white px-3 py-2 rounded-lg transition-colors text-sm font-medium hidden md:block">
              {contactLabel}
            </a>
          )}
          <a href={appLinks.login} className="text-slate-300 hover:text-white px-3 py-2 rounded-lg transition-colors text-sm font-medium hidden sm:block">
            Sign In
          </a>
          <a href={appLinks.register} className="btn btn-primary text-sm shadow-lg shadow-primary-500/20">
            Get Started
          </a>
        </div>
      </nav>
    </header>
  );
}
